import { type MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { observable } from "mobx";

import {
  apiGetBillingScreeningUsers,
  apiGetPatients,
  apiPutPatientsPatientId,
  ListPatientsResponse,
  PatientMonitoringType,
  PatientStatus,
  PermissionBit,
} from "../../api";
import { maybeReportError } from "../../app/errors";
import { location } from "../../app/location";
import { hasPermission } from "../../constants/permissions";
import { DataModel } from "../../models/data-model";
import { consentOk } from "../../utils/data-utils";
import { Merge } from "../../utils/types";

export type FullPatientsItem = Merge<
  ListPatientsResponse["patients"][number]
> & {
  // Joined in on the Screening tab, where the patient record is a placeholder
  // and the screening user's login is the only thing that identifies it.
  screening_username?: string;
  screening_email?: string | null;
};

export interface TabQuery {
  patient_status?: PatientStatus[];
  monitoring_type?: PatientMonitoringType[];
}

export class PatientsTabModel extends DataModel<FullPatientsItem> {
  readonly idAttr = "patient_id" as const;
  @observable accessor loading = false;
  /** Why the Screening tab's usernames are missing, when their lookup failed. */
  @observable accessor screeningUsersError: unknown = undefined;
  private loaded = false;
  private abortController: AbortController | undefined;
  readonly screening: boolean;
  private readonly query: TabQuery;
  private readonly getAssignedOnly: () => boolean;

  // Assigned by hand rather than as constructor parameter properties — see
  // the note in `admin/users/users.ts`: with decorators in play, a parameter
  // property lands above `super()` in a production build.
  constructor(
    def: TabDef,
    getAssignedOnly: () => boolean,
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super([], permissions, userUUID);
    this.query = def.query;
    this.screening = def.screening ?? false;
    this.getAssignedOnly = getAssignedOnly;
  }

  async loadIfNeeded() {
    if (this.loaded || this.loading) return;
    this.loading = true;
    this.abortController?.abort();
    const abort = new AbortController();
    this.abortController = abort;
    try {
      const [res, screeningUsers] = await Promise.all([
        apiGetPatients({
          query: {
            load_type: "basic",
            paginate: false,
            // Screening bundles are never assigned to staff, so the filter
            // would always empty the tab.
            ...(this.getAssignedOnly() && !this.screening
              ? { assigned_only: true }
              : {}),
            ...this.query,
          },
          signal: abort.signal,
        }),
        this.screening ? this.loadScreeningUsers(abort.signal) : [],
      ]);
      if (abort.signal.aborted) return;
      const userByPatient = new Map(
        screeningUsers.flatMap((user) =>
          user.patient_ids.map((id) => [id, user] as const),
        ),
      );
      this.updateData(
        res.data.patients.map((patient: FullPatientsItem) => {
          const user = userByPatient.get(patient.patient_id);
          return user
            ? {
                ...patient,
                screening_username: user.username,
                screening_email: user.email,
              }
            : patient;
        }),
      );
      this.loaded = true;
    } catch (err) {
      if (abort.signal.aborted) return;
      throw err;
    } finally {
      if (!abort.signal.aborted) {
        this.loading = false;
      }
    }
  }

  // The rows are still worth showing without their usernames, so a failed
  // lookup leaves those cells blank and explains why, instead of failing the
  // whole tab.
  private async loadScreeningUsers(signal: AbortSignal) {
    this.screeningUsersError = undefined;
    try {
      const res = await apiGetBillingScreeningUsers({
        query: { paginate: false },
        signal,
      });
      return res.data.screening_users;
    } catch (err) {
      if (!signal.aborted) {
        maybeReportError(err);
        this.screeningUsersError = err;
      }
      return [];
    }
  }

  invalidate() {
    this.abortController?.abort();
    this.loaded = false;
    this.loading = false;
  }

  async updatePatient(patId: number, field: object) {
    await apiPutPatientsPatientId({
      path: { patient_id: patId },
      body: field,
    });
    location.reload();
  }

  disabledPatLink(patient: FullPatientsItem) {
    return (
      !consentOk(patient) &&
      !hasPermission(this.permissions ?? [], PermissionBit.BYPASS_CONSENT)
    );
  }
}

export interface TabDef {
  id: string;
  label: MessageDescriptor;
  requiredPermission?: PermissionBit;
  query: TabQuery;
  /** Rows are screening users' PPG bundles, identified by the user's login. */
  screening?: boolean;
}

export const TAB_DEFS: TabDef[] = [
  {
    id: "active",
    label: msg({ message: "Active", context: "patients tab" }),
    query: {
      patient_status: [PatientStatus.active],
      monitoring_type: [PatientMonitoringType.self_measurement],
    },
  },
  {
    // Pre-first-measurement patients. They have no watch status, schedule or
    // alerts, so this tab is the only place they surface — hence both
    // monitoring types, so a research patient can't fall through every tab
    // (Research is `active`-only) while still awaiting its first measurement.
    id: "onboarded",
    label: msg({ message: "Onboarded", context: "patients tab" }),
    query: {
      patient_status: [PatientStatus.onboarded],
      monitoring_type: [
        PatientMonitoringType.self_measurement,
        PatientMonitoringType.physician_supervision,
      ],
    },
  },
  {
    id: "research",
    label: msg`Research`,
    requiredPermission: PermissionBit.CLINICAL_STUDIES,
    query: {
      patient_status: [PatientStatus.active],
      monitoring_type: [PatientMonitoringType.physician_supervision],
    },
  },
  {
    id: "not_active",
    label: msg`Not Active`,
    query: {
      patient_status: [
        PatientStatus.not_active,
        PatientStatus.archived,
        PatientStatus.deceased,
      ],
      monitoring_type: [
        PatientMonitoringType.self_measurement,
        PatientMonitoringType.physician_supervision,
      ],
    },
  },
  {
    id: "test",
    label: msg`Test`,
    requiredPermission: PermissionBit.GROUP_ADMIN_ROUTERS,
    query: {
      monitoring_type: [PatientMonitoringType.testing],
    },
  },
  {
    // Not patients: each bundles the PPGs of one screening user. The API leaves
    // them out unless asked for by monitoring type.
    id: "screening",
    label: msg({ message: "Screening", context: "patients tab" }),
    requiredPermission: PermissionBit.GROUP_ADMIN_ROUTERS,
    query: {
      monitoring_type: [PatientMonitoringType.screening],
    },
    screening: true,
  },
];
